import React from "react";
import axios from "axios";
import { transportBodyTypes } from "../../components/search/categories";

/** Stroke icon (wireframe icon set, 24x24 viewBox). */
export const Icon = ({ d, className = "icon", children, ...rest }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.7"
    viewBox="0 0 24 24"
    {...rest}
  >
    {d ? <path d={d} /> : children}
  </svg>
);

export const P = {
  pin: "M19 9c0 6-7 12-7 12S5 15 5 9a7 7 0 1 1 14 0 M14 9a2 2 0 1 1-4 0 2 2 0 0 1 4 0",
  x: "m7 7 10 10 M7 17 17 7",
  cal: "M4 5h16v16H4z M4 10h16 M8 2v6 M16 2v6",
  chevron: "m7 10 5 5 5-5",
  truck: "M2 5h12v13H2z M14 10h4l4 5v3h-8 M5 18a2 2 0 1 0 .01 0 M17 18a2 2 0 1 0 .01 0",
  weight: "M5 5h14v17H5z M5 10h14 M9 5V3h6v7 M12 10v4",
  box: "m12 3 9 5-9 5-9-5z M3 8v10l9 5 9-5V8 M12 13v10 M8 5l9 5",
  card: "M3 5h18v15H3z M3 10h18 M6 15h4",
  search: "M16 10a6 6 0 1 1-12 0 6 6 0 0 1 12 0 M15 15l6 6",
  expand: "M3 9V3h6 M15 21h6v-6 M3 3l7 7 M21 21-7-7",
  target: "M18 12a6 6 0 1 1-12 0 6 6 0 0 1 12 0 M12 2v4 M12 18v4 M2 12h4 M18 12h4 M13 12a1 1 0 1 1-2 0 1 1 0 0 1 2 0",
  list: "M7 6h15 M7 12h15 M7 18h15 M2 6h1 M2 12h1 M2 18h1",
  map: "m3 5 6-2 6 2 6-2v16l-6 2-6-2-6 2z M9 3v16 M15 5v16",
  chat: "M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7l-5 3V7a3 3 0 0 1 3-3 M8 9h7 M8 13h3",
  check: "m5 12 4 4L19 6",
  arrow: "M4 12h16 M14 6l6 6-6 6",
  user: "M15 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0 M5 21v-3a7 7 0 0 1 14 0v3 M18 4a3 3 0 0 1 0 6 M21 20v-5",
  globe: "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0 M3 12h18 M12 3c-5 5-5 13 0 18 M12 3c5 5 5 13 0 18 M5 7h14 M5 17h14",
  bell: "M5 17h14l-2-4V9a5 5 0 0 0-10 0v4z M10 21h4",
  gear: "m9 3 6 0 1 3 3 1 2 5-2 5-3 1-1 3H9l-1-3-3-1-2-5 2-5 3-1z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0",
};

export const API = process.env.REACT_APP_API_URL;
export const authHeaders = () => ({
  Authorization: `JWT ${window.localStorage.getItem("access")}`,
});

/** All orders (same endpoint the legacy search/my-orders pages use). */
export const fetchAllOrders = async () => {
  const { data } = await axios({
    url: `${API}/order_search/`,
    method: "POST",
    data: {},
    headers: authHeaders(),
  });
  return Array.isArray(data) ? data : [];
};

export const CURRENCY = { 1: "USD", 2: "RUB", 3: "KZT", 4: "KGS" };
export const CURRENCY_SYMBOL = { 1: "$", 2: "₽", 3: "₸", 4: "сом" };
export const currencyLabel = (c) => CURRENCY_SYMBOL[c] || "";
/** Selectable currencies (id -> code): сом first/default. RUB stays in CURRENCY/CURRENCY_SYMBOL only to display legacy data. */
export const CURRENCY_OPTIONS = [
  ["4", "KGS"],
  ["1", "USD"],
  ["3", "KZT"],
]; // array: integer-like object keys would be re-sorted ascending
/** Currency code from the matching API ('KGS'/'USD'/…) → sign («сом»/«$»/«₸»); unknown codes are shown as is. */
const CODE_SIGN = { KGS: "сом", USD: "$", KZT: "₸", RUB: "₽" };
export const codeSign = (code) => (code ? CODE_SIGN[String(code).toUpperCase()] || String(code) : "");

export const ORDER_TYPES = {
  1: "Оборудование",
  2: "Стройматериалы",
  3: "Металл",
  4: "Продукты",
  5: "Запчасти",
  6: "Сырьё",
  7: "Мебель",
  8: "Металлопрокат",
  9: "Трубы",
};
export const PACKAGE_TYPES = {
  1: "EUR-палеты",
  2: "FIN-палеты",
  3: "Особые палеты",
  4: "Коробки",
  5: "Контейнер",
  6: "Без упаковки",
};
export const LOAD_TYPES = { 1: "Верхняя", 2: "Боковая", 3: "Задняя" };

export const bodyTypeName = (id) =>
  transportBodyTypes.find((b) => b.id === Number(id))?.name || "";
/**
 * Selectable body types (children only). The source list has repeats: the same name twice under one
 * group (Пищевая-сыпучая 23/24 — the second is dropped from the options, its id still resolves by bodyTypeName)
 * and the same name under different groups (Самосвал 11/36 — kept, labelled with the group).
 */
export const BODY_TYPE_OPTIONS = (() => {
  const kids = transportBodyTypes.filter((b) => b.parent_id !== null);
  const seen = new Set();
  const uniq = kids.filter((b) => {
    const k = `${b.parent_id}|${b.name}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const count = {};
  uniq.forEach((b) => (count[b.name] = (count[b.name] || 0) + 1));
  return uniq.map((b) => {
    if (count[b.name] < 2) return b;
    const group = transportBodyTypes.find((g) => g.id === b.parent_id)?.name;
    return group ? { ...b, name: `${b.name} (${group})` } : b;
  });
})();

export const cityOf = (addr) => (addr || "").split(",")[0].trim() || "—";

export const nf = (n) =>
  Number(n).toLocaleString("ru-RU", { maximumFractionDigits: 1 }).replace(/ /g, " ");

export const money = (price, currency) =>
  price ? `${nf(price)} ${currencyLabel(currency)}`.trim() : null;

const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
export const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(s.length <= 10 ? `${s}T00:00:00` : s);
  return isNaN(d) ? null : d;
};
export const fmtDateShort = (s) => {
  const d = parseDate(s);
  return d ? `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}` : "—";
};
export const fmtDateLong = (s) => {
  const d = parseDate(s);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : "—";
};
export const fmtDateTime = (s) => {
  const d = parseDate(s);
  if (!d) return "—";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
};
export const isoDate = (d) => {
  const z = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
};

/** Order not yet expired (to_date today or later). */
export const notExpired = (o) => {
  if (!o.to_date) return true;
  const d = parseDate(o.to_date);
  if (!d) return true;
  d.setHours(23, 59, 59, 999);
  return d >= new Date();
};

/**
 * Matching-service TTL: a load is live for `ttlHours` (default 12) from created_at.
 * Owners' extensions (confirmed_at) are not exposed to other users, so this is a
 * client-side approximation for other people's loads.
 */
export const DEFAULT_LOAD_TTL_HOURS = 12;
export const ttlActive = (o, ttlHours = DEFAULT_LOAD_TTL_HOURS) => {
  const base = parseDate(o.confirmed_at || o.created_at);
  if (!base) return true;
  return Date.now() < base.getTime() + ttlHours * 3600 * 1000;
};

export const shortId = (o) => String(o.uuid || "").toUpperCase();
